/**
 * Standalone auto-add helpers for keeping report flows in sync as jobs / reports
 * change in the system. These do NOT depend on React hooks so they can be
 * imported safely from useJobs (which sits outside the report management
 * context) and from one-off form handlers without introducing circular deps.
 *
 * Strategy:
 *   1. Fetch the affected job (id, company_id, job_type, created_at, job_reports).
 *   2. Fetch all active+non-archived flows for that job's company.
 *   3. For each flow, decide which (job, report) tuples should exist using
 *      matchFlowScope() — same logic as initial population.
 *   4. De-dupe against existing report_flow_items rows.
 *   5. Bulk insert any missing rows into the FIRST column of each flow.
 *
 * Best-effort: every step is wrapped so a failure here NEVER blocks the
 * caller's primary operation (creating/updating a job, saving reports, etc.).
 */

import { hasuraClient } from './graphqlClient';
import { matchFlowScope } from './flowScopeMatcher';
import { BULK_CREATE_REPORT_FLOW_ITEMS } from '../graphql/mutations/reportManagement';

// ─── Local queries (kept here so this file is self-contained for tree-shaking) ──

const GET_JOB_FOR_AUTO_ADD = `
  query GetJobForAutoAdd($id: uuid!) {
    jobs_by_pk(id: $id) {
      id
      job_type
      # matchJobType once job_type_id'ye bakar — legacy metin kolonu
      # denetimsiz serbest metne dondugu icin tek basina guvenilmez.
      job_type_id
      job_type_rel { id name }
      # job_status — matchFlowScope JOB_LIST branch'inde is durumu filter'i
      # icin bu alani okur. Onceden fetch edilmiyordu, job.job_status
      # undefined kaliyordu ve autoAdd icin filter'a takilmasa da
      # flow.job_statuses de bos gelince (asagida) filter tamamen atlanip
      # her is akisa ekleniyordu (bug: Andirin firmasi YAZIMDA statusuyle
      # TEKNOPARK+ONAYLANDI akisina eklenmisti, history'de hic ONAYLANDI
      # olmamis).
      job_status
      company_id
      created_at
      job_reports
    }
  }
`;

const GET_ACTIVE_FLOWS_FOR_COMPANY = `
  query GetActiveFlowsForAutoAdd($company_id: Int!) {
    report_flows(
      where: {
        company_id: { _eq: $company_id }
        is_active: { _eq: true }
        is_archived: { _eq: false }
      }
    ) {
      id
      flow_title
      job_types
      # job_statuses — akis iş durumu daraltmasi ("is Listesi Filtresi >
      # Is Durumu: Onaylandi"). Onceden fetch edilmiyordu → undefined →
      # matchFlowScope statuses=[] sanip filter'i tamamen atliyordu.
      # Bu yuzden her is her akisa ekleniyordu (yazimda dahil).
      job_statuses
      source_type
      filter_config
      flow_type
      start_date
      end_date
      report_type_ids
      is_active
      is_archived
      company_id
      report_flow_columns(order_by: { column_order: asc }, limit: 1) {
        id
      }
    }
  }
`;

const GET_EXISTING_FLOW_ITEMS_FOR_JOB = `
  query GetExistingFlowItemsForJob($job_id: uuid!, $flow_ids: [Int!]!) {
    report_flow_items(
      where: {
        job_id: { _eq: $job_id }
        flow_id: { _in: $flow_ids }
      }
    ) {
      id
      flow_id
      job_id
      report_id
      is_archived
    }
  }
`;

/**
 * Build the deduplication key used to compare a target tuple against existing
 * report_flow_items rows. Mirrors the populateFlowItems convention so the two
 * code paths cannot drift.
 */
const buildKey = (flowId, jobId, reportId) =>
  `${flowId}|${jobId}|${reportId == null ? '' : String(reportId)}`;

/**
 * Auto-add the given job (and any of its reports that are in scope) to all
 * matching active flows for the job's company.
 *
 * @param {string} jobId  uuid of the job that was just created/updated
 * @returns {Promise<{ inserted: number, flowsTouched: number }>} summary
 */
export async function autoAddJobToFlowsStandalone(jobId) {
  // Verification log: lets you see in the browser console that the trigger
  // actually fired from useJobs / JobReportingPlanModal / UpdateReportStatusDialog.
  // eslint-disable-next-line no-console
  console.log('[autoAdd] triggered for', jobId);

  if (!jobId) {
    console.warn('[autoAdd] aborted — no jobId provided');
    return { inserted: 0, flowsTouched: 0 };
  }

  // 1) Fetch the job. We fetch by PK so a missing job_reports field does not
  // exclude the job (this is what makes JOB_LIST flows work even for jobs
  // without any reports defined).
  let job;
  try {
    const res = await hasuraClient.query(GET_JOB_FOR_AUTO_ADD, { id: jobId });
    job = res?.jobs_by_pk;
  } catch (err) {
    console.error('[autoAdd] failed to fetch job', jobId, err);
    return { inserted: 0, flowsTouched: 0 };
  }
  if (!job || !job.company_id) {
    console.warn('[autoAdd] job not found or has no company_id', jobId);
    return { inserted: 0, flowsTouched: 0 };
  }

  // 2) Fetch all active+non-archived flows for the company
  let flows = [];
  try {
    const res = await hasuraClient.query(GET_ACTIVE_FLOWS_FOR_COMPANY, {
      company_id: job.company_id,
    });
    flows = res?.report_flows || [];
  } catch (err) {
    console.error('[autoAdd] failed to fetch flows', err);
    return { inserted: 0, flowsTouched: 0 };
  }
  if (flows.length === 0) {
    console.log('[autoAdd] no active flows for company', job.company_id);
    return { inserted: 0, flowsTouched: 0 };
  }

  // 3) Build target tuples per flow using matchFlowScope
  const targets = []; // { flow, columnId, reportId|null }
  for (const flow of flows) {
    const firstColumnId = flow.report_flow_columns?.[0]?.id;
    if (!firstColumnId) continue; // flow has no columns yet — nothing we can do

    if (flow.source_type === 'JOB_LIST') {
      if (matchFlowScope(flow, job, null)) {
        targets.push({ flow, columnId: firstColumnId, reportId: null });
      }
    }
    // REPORT_PLAN flows intentionally skipped: matching reports show up in
    // the Backlog and require manual drag-in. Avoiding auto-insert here keeps
    // the UX consistent with populateFlowItems (also skipped for REPORT_PLAN).
  }

  if (targets.length === 0) {
    console.log('[autoAdd] no matching flow targets for job', jobId);
    return { inserted: 0, flowsTouched: 0 };
  }

  // 4) Dedupe — fetch existing items for this job across the candidate flows
  const flowIds = Array.from(new Set(targets.map((t) => t.flow.id)));
  let existingItems = [];
  try {
    const res = await hasuraClient.query(GET_EXISTING_FLOW_ITEMS_FOR_JOB, {
      job_id: jobId,
      flow_ids: flowIds,
    });
    existingItems = res?.report_flow_items || [];
  } catch (err) {
    console.error('[autoAdd] failed to fetch existing items', err);
    return { inserted: 0, flowsTouched: 0 };
  }

  const existingKeys = new Set(
    existingItems.map((it) => buildKey(it.flow_id, it.job_id, it.report_id))
  );

  const objectsToInsert = [];
  const flowsTouched = new Set();
  for (const t of targets) {
    const key = buildKey(t.flow.id, jobId, t.reportId);
    if (existingKeys.has(key)) continue;
    objectsToInsert.push({
      flow_id: t.flow.id,
      column_id: t.columnId,
      job_id: jobId,
      report_id: t.reportId,
      is_archived: false,
      company_id: job.company_id,
      // created_by intentionally omitted: caller may not have a user context
      // (e.g. background flow). Hasura nullable column accepts undefined.
    });
    flowsTouched.add(t.flow.id);
  }

  if (objectsToInsert.length === 0) {
    console.log(
      '[autoAdd] nothing new to insert for job',
      jobId,
      `(${targets.length} target(s) already present)`,
    );
    return { inserted: 0, flowsTouched: 0 };
  }

  // 5) Bulk insert
  try {
    const res = await hasuraClient.mutate(BULK_CREATE_REPORT_FLOW_ITEMS, {
      objects: objectsToInsert,
    });
    const inserted = res?.insert_report_flow_items?.affected_rows || 0;
    console.log(
      `[autoAdd] inserted ${inserted} item(s) into ${flowsTouched.size} flow(s) for job`,
      jobId,
    );
    return {
      inserted,
      flowsTouched: flowsTouched.size,
    };
  } catch (err) {
    console.error('[autoAdd] bulk insert failed', err);
    return { inserted: 0, flowsTouched: 0 };
  }
}

/**
 * Auto-add a single report (within a job) to all matching active flows.
 * Currently delegates to autoAddJobToFlowsStandalone because the matcher
 * naturally filters down to relevant reports — keeping the surface area
 * narrow. Provided for explicit semantics in callers that only changed
 * a single report.
 *
 * @param {string} jobId
 * @param {string|number} _reportId   currently unused — kept for API stability
 * @returns {Promise<{ inserted: number, flowsTouched: number }>}
 */
export async function autoAddReportToFlowsStandalone(jobId, _reportId) {
  return autoAddJobToFlowsStandalone(jobId);
}

export default autoAddJobToFlowsStandalone;
